import { FC, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Spinner } from 'react-bootstrap-v5'
import { parse } from 'query-string'
import axios from 'axios'
import { AlexiRoutes } from '../../../utils/constants/AlexiRoutes.enum'
import { getIntegrationURLFromENV } from '../../../utils/getIntegrationURLFromENV'
import { setPartnerDetails } from './redux/actions'
import { useDispatch } from 'react-redux'

/**
 * Verification states
 */
type VerificationState =
  | 'verifying' // Verifying signature with backend
  | 'valid_redirecting' // Found valid application, redirecting
  | 'invalid_signature' // Signature verification failed
  | 'no_application' // No valid application found
  | 'missing_params' // Required parameters not provided
  | 'error' // Generic error

const NetsEntryVerification: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const [state, setState] = useState<VerificationState>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const getPartnerDetails = useCallback(
    async (partnerId: string) => {
      const env = localStorage.getItem('env') || ''
      const API_URL = getIntegrationURLFromENV(env)
      if (env === 'prod') {
        partnerId =
          partnerId === undefined || partnerId === null
            ? (process.env.REACT_APP_ALEXI_DEFAULT_PARTNERID as string)
            : partnerId
      }
      const partnerData = await axios.get(`${API_URL}ext/api/businesspartner/${partnerId || ''}`)

      if (partnerData.data && partnerData.status === 200) {
        localStorage.setItem('partnerId', partnerData.data?.id)
        localStorage.setItem('partnerDetails', JSON.stringify(partnerData.data))
        dispatch(setPartnerDetails(partnerData.data))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    const verifyAndGetApplication = async () => {
      try {
        // 1. Extract parameters from URL
        const searchQuery = parse(location.search)

        // Check if we already have an application ID in URL (from previous redirect)
        if (searchQuery.id) {
          // Application ID exists, redirect to the application flow
          history.replace(`/${AlexiRoutes.SG_NETS_ONBOARDING}/${searchQuery.id}`)
          return
        }

        // Extract NETS parameters from query string
        const org_id = searchQuery.uen as string
        const client_id = searchQuery.client_id as string
        const timestamp = searchQuery.expires_at as string
        const nonce = searchQuery.nonce as string
        const signature = searchQuery.signature as string
        // Check for required parameters
        if (!org_id || !timestamp || !signature || !client_id || !nonce) {
          setState('missing_params')
          setErrorMessage('Missing required parameters. Please use the correct referral link.')
          return
        }

        // 2. Call backend API to verify signature and get application data
        const env = localStorage.getItem('env') || ''
        const envFromQuery: any = searchQuery?.env

        localStorage.setItem('env', envFromQuery || env)
        const API_URL = getIntegrationURLFromENV(
          typeof envFromQuery === 'string' ? envFromQuery : env
        )

        const response = await axios.post(
          `${API_URL}nets/verify-signature`,
          {}, // Empty body, all data in headers
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-uen': org_id,
              'x-auth-expires-at': timestamp,
              'x-auth-nonce': nonce,
              'x-auth-signature': signature,
              'x-auth-client-id': client_id,
            },
          }
        )
        if (response.data && response.status === 201) {
          const application = response.data

          console.log('NetsEntryVerification - application:', application)
          console.log('NetsEntryVerification - amountRequested:', application?.amountRequested)

          // Check if application exists and is valid
          if (application && application.id) {
            // Valid application found - redirect with ID in URL
            localStorage.setItem('partnerId', application.partnerId)
            getPartnerDetails(application.partnerId)
            setState('valid_redirecting')

            const redirectUrl = `/${AlexiRoutes.SG_NETS_ONBOARDING}?mobile=${encodeURIComponent(
              application.mobileNumber
            )}&name=${encodeURIComponent(application.applicantName)}`

            history.replace(redirectUrl)
          } else {
            // No valid application found
            setState('no_application')
          }
        } else {
          // Signature verification failed
          setState('invalid_signature')
          setErrorMessage(
            response.data?.message || 'Invalid signature. Please use the correct referral link.'
          )
        }
      } catch (error) {
        console.error('Verification error:', error)
        if (axios.isAxiosError(error) && error.response) {
          // Backend returned an error
          setState('invalid_signature')
          setErrorMessage(
            error.response.data?.message ||
              'Verification failed. Please use the correct referral link.'
          )
        } else {
          setState('error')
          setErrorMessage('An error occurred during verification. Please try again.')
        }
      }
    }

    verifyAndGetApplication()
  }, [location.search, history, getPartnerDetails])

  // Render based on state
  const renderContent = () => {
    switch (state) {
      case 'verifying':
        return (
          <div className='text-center py-10'>
            <Spinner animation='grow' variant='danger' />
            <p className='mt-4 text-muted'>Verifying referral link...</p>
          </div>
        )

      case 'valid_redirecting':
        return (
          <div className='text-center py-10'>
            <Spinner animation='grow' variant='danger' />
            <p className='mt-4 text-muted'>Redirecting to application...</p>
          </div>
        )

      case 'no_application':
        return (
          <div className='container py-10'>
            <div className='alert alert-warning' role='alert'>
              <h4 className='alert-heading'>Application Not Found</h4>
              <p>
                We could not find a valid application associated with this referral. Please ensure
                you have an active application in progress.
              </p>
              <hr />
              <p className='mb-0'>If you believe this is an error, please contact NETS support.</p>
            </div>
          </div>
        )

      case 'invalid_signature':
      case 'missing_params':
        return (
          <div className='container py-10'>
            <div className='alert alert-danger' role='alert'>
              <h4 className='alert-heading'>Verification Failed</h4>
              <p>{errorMessage}</p>
              <hr />
              <p className='mb-0'>
                Please use the referral link provided by NETS or contact support for assistance.
              </p>
            </div>
          </div>
        )

      case 'error':
        return (
          <div className='container py-10'>
            <div className='alert alert-danger' role='alert'>
              <h4 className='alert-heading'>Error</h4>
              <p>{errorMessage}</p>
              <hr />
              <p className='mb-0'>Please try again later or contact support for assistance.</p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className='nets-verification-wrapper min-h-screen bg-light'>
      <div className='py-8'>{renderContent()}</div>
    </div>
  )
}

export default NetsEntryVerification
